import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import type frTranslations from './translations/fr.json';

export type GlobalTranslations = typeof frTranslations;

export const defaultNS = 'global' as const;
export const supportedLanguages = ['fr', 'en'] as const;

const PREFERRED_LANGUAGE_KEY = 'preferredLanguage';

let initPromise: Promise<void> | null = null;

async function doInit(): Promise<void> {
	const languageToUse = (() => {
		const browserLanguage = navigator.language.split('-')[0];
		const savedLanguage = localStorage.getItem(PREFERRED_LANGUAGE_KEY);
		if (savedLanguage) {
			return savedLanguage as (typeof supportedLanguages)[number];
		}

		if (
			supportedLanguages.includes(
				browserLanguage as (typeof supportedLanguages)[number],
			)
		) {
			return browserLanguage as (typeof supportedLanguages)[number];
		}

		return 'fr';
	})();

	const module = (await import(`./translations/${languageToUse}.json`)) as {
		default: GlobalTranslations;
	};
	const resources = {
		[languageToUse]: {
			[defaultNS]: module.default,
		},
	};

	await i18n.use(initReactI18next).init({
		resources,
		lng: languageToUse,
		fallbackLng: 'en',
		defaultNS,
		interpolation: {
			escapeValue: false,
		},
	});

	document.documentElement.lang = languageToUse;
	localStorage.setItem(PREFERRED_LANGUAGE_KEY, languageToUse);

	i18n.on('languageChanged', (language) => {
		document.documentElement.lang = language;
		localStorage.setItem(PREFERRED_LANGUAGE_KEY, language);
		if (!i18n.hasResourceBundle(language, defaultNS)) {
			void import(`./translations/${language}.json`).then(
				(mod: { default: GlobalTranslations }) => {
					i18n.addResourceBundle(language, defaultNS, mod.default);
				},
			);
		}
	});
}

export function initI18n(): Promise<void> {
	if (!initPromise) {
		initPromise = doInit();
	}
	return initPromise;
}

export async function changeLanguage(lng: string): Promise<void> {
	if (
		!supportedLanguages.includes(lng as (typeof supportedLanguages)[number])
	) {
		return;
	}
	if (!i18n.hasResourceBundle(lng, defaultNS)) {
		const mod = (await import(`./translations/${lng}.json`)) as {
			default: GlobalTranslations;
		};
		i18n.addResourceBundle(lng, defaultNS, mod.default);
	}
	await i18n.changeLanguage(lng);
}

export default i18n;

if (import.meta.hot) {
	import.meta.hot.accept(
		['./translations/fr.json', './translations/en.json'],
		(updatedModules) => {
			const [updatedFrTranslation, updatedEnTranslation] = updatedModules;

			if (updatedFrTranslation) {
				i18n.addResourceBundle('fr', defaultNS, updatedFrTranslation.default);
				i18n.emit('languageChanged', 'fr');
			}
			if (updatedEnTranslation) {
				i18n.addResourceBundle('en', defaultNS, updatedEnTranslation.default);
				i18n.emit('languageChanged', 'en');
			}
		},
	);
}
